import { AggregateClient, type AggregateClientDeps } from './aggregateClient.js';
import { signedRequestWithRetry, type RequestDeps } from './http.js';
import { throwForStatus, WireVersionMismatch } from './errors.js';
import { createNonceSource } from './headers.js';
import { subscribe, type EventSourceLike, type SubscribeOptions } from './subscribe.js';
import { DEFAULT_RETRY, WIRE_VERSION, type AggregateChange, type AggregateRef, type AuthProvider, type RetryPolicy } from './types.js';

interface IndexResponseJson {
  aggregates: { aggregate_type: string; aggregate_id: string }[];
}

export interface GatewayOptions {
  baseUrl: string;
  auth: AuthProvider;
  fetch?: typeof globalThis.fetch;
  eventSourceFactory?: (url: string) => EventSourceLike;
  retry?: Partial<RetryPolicy>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

export class Gateway {
  private readonly baseUrl: string;
  private readonly fetchFn: typeof globalThis.fetch;
  private readonly auth: AuthProvider;
  private readonly retry: RetryPolicy;
  private readonly now: () => number;
  // One monotonic nonce source shared by every request and SSE handshake from this Gateway,
  // so no two carry the same (client_id, nonce) and trip the gateway's replay 401 (W1).
  private readonly nextNonce: () => string;
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly eventSourceFactory: (url: string) => EventSourceLike;

  constructor(options: GatewayOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '');
    this.auth = options.auth;
    this.fetchFn = options.fetch ?? globalThis.fetch.bind(globalThis);
    this.now = options.now ?? Date.now;
    this.nextNonce = createNonceSource(this.now);
    this.sleep = options.sleep ?? ((ms) => new Promise((r) => setTimeout(r, ms)));
    this.retry = { ...DEFAULT_RETRY, ...options.retry };
    this.eventSourceFactory = options.eventSourceFactory ?? defaultEventSourceFactory;
  }

  aggregate(type: string, id: string): AggregateClient {
    if (type.length === 0) throw new Error('aggregate type must not be empty');
    if (id.length === 0) throw new Error('aggregate id must not be empty');
    const deps: AggregateClientDeps = {
      baseUrl: this.baseUrl,
      fetchFn: this.fetchFn,
      auth: this.auth,
      retry: this.retry,
      now: this.now,
      nextNonce: this.nextNonce,
      sleep: this.sleep,
    };
    return new AggregateClient(deps, type, id);
  }

  // Lists the aggregates the authenticated principal (the user if logged in, else this device's
  // client) currently holds, in the resolved org, optionally filtered by type. There is no
  // principal parameter: a caller can only ever list its own. Use this to discover a user's
  // aggregates on a fresh device where local state is empty. The list is a discovery hint, not an
  // authority: an entry can still 404 (deleted) or 403 (access changed), so confirm on open.
  async listIndex(aggregateType?: string): Promise<AggregateRef[]> {
    const params = new URLSearchParams();
    if (aggregateType !== undefined) params.set('aggregate_type', aggregateType);
    const qs = params.toString();
    const url = `${this.baseUrl}/api/v1/index${qs.length > 0 ? `?${qs}` : ''}`;
    const response = await signedRequestWithRetry(this.requestDeps(), url, { method: 'GET' });
    await throwForStatus(response, url);
    const got = response.headers.get('X-Wire-Version');
    if (got !== null && got !== WIRE_VERSION) throw new WireVersionMismatch(url, WIRE_VERSION, got);
    const body = (await response.json()) as IndexResponseJson;
    return body.aggregates.map((a) => ({ aggregateType: a.aggregate_type, aggregateId: a.aggregate_id }));
  }

  private requestDeps(): RequestDeps {
    return { fetchFn: this.fetchFn, auth: this.auth, retry: this.retry, nextNonce: this.nextNonce, sleep: this.sleep };
  }

  subscribe(type: string, ids: string[], options: SubscribeOptions = {}): AsyncGenerator<AggregateChange, void, unknown> {
    return subscribe(
      {
        baseUrl: this.baseUrl,
        auth: this.auth,
        eventSourceFactory: this.eventSourceFactory,
        nextNonce: this.nextNonce,
      },
      type,
      ids,
      options,
    );
  }
}

function defaultEventSourceFactory(url: string): EventSourceLike {
  // Native EventSource is global in browsers and Node 22+.
  const ctor = (globalThis as { EventSource?: new (u: string) => EventSourceLike }).EventSource;
  if (ctor === undefined) {
    throw new Error('Global EventSource is not available; pass eventSourceFactory in GatewayOptions');
  }
  return new ctor(url);
}
